import { ColDef, GetRowIdFunc, GridOptions } from "ag-grid-community";
import React from "react";

/**
 * Идентификатор строки для AG Grid.
 *
 * До версии 27 это был проп `getRowNodeId`, которому приходили сами данные строки;
 * теперь `getRowId`, и ему приходит объект с полем `data`. Обёртка нужна, чтобы
 * существующие функции остались как есть: их зовут и напрямую, с данными строки.
 *
 * Результат стабилен по ссылке, поэтому вызывать её надо один раз на модуль или
 * в поле класса, а не в разметке: `getRowId` относится к начальным настройкам грида.
 *
 * Данные строки здесь `any` намеренно: гриды в проекте не типизированы по строке,
 * и обобщённый параметр начал бы выводить тип строки из этой функции — а он расходится
 * с типом `rowData` (снапшот MST против инстанса).
 */
export const rowIdBy =
  (getId: (data: any) => string): GetRowIdFunc =>
  ({ data }) =>
    getId(data);

export const RowNumberColumn = (selectable = false, headerName = "№"): ColDef => ({
  headerName,
  valueGetter: "node.rowIndex + 1",
  width: selectable ? 50 : 50,
  sortable: false,
  resizable: false,
  cellClass: "righted row-number",
  checkboxSelection: selectable,
});

export const FIGMA_TABLE_ICON_SIZE = 10;

// Перегрузки нужны из-за `icons` в GridOptions: AG Grid ждёт там строку или функцию,
// а без них выводился союз `string | Element` — и разметка иконки не подходила по типу
export function TableIcon(name: string, size?: number): string;
export function TableIcon(name: string, size: number, asString: false): React.JSX.Element;
export function TableIcon(name: string, size = 14, asString = true) {
  return asString ? (
    `<i class="fa ${name}" aria-hidden="true" style="font-size:${size}px; width:${size}px; height:${size}px; color: #182026"></i>`
  ) : (
    <i
      className={`fa ${name}`}
      aria-hidden="true"
      style={{
        fontSize: `${size}px`,
        width: `${size}px`,
        height: `${size}px`,
        color: "#182026",
      }}
    />
  );
}

/**
 * Меню колонки прежнего вида.
 *
 * В версии 31 у колонки появилась отдельная кнопка фильтра рядом с «гамбургером»,
 * а само меню переехало на новый вид. Оставляем прежний: иначе в шапке возникает
 * элемент управления, которого в интерфейсе не было.
 *
 * Значение объявлено устаревшим и убрано в 33-й версии — вернуться к этому
 * придётся на следующем подъёме.
 */
const LEGACY_COLUMN_MENU = "legacy" as const;

export const DefaultAgGridOptions = (figma = false): GridOptions => ({
  icons: {
    sortAscending: TableIcon("fa-caret-up", figma ? FIGMA_TABLE_ICON_SIZE : 14),
    sortDescending: TableIcon("fa-caret-down", figma ? FIGMA_TABLE_ICON_SIZE : 14),
    // checkboxChecked: '<GeneralIcon type="general-checkbox" />',
    // checkboxUnchecked: '<GeneralIcon type="general-zero-checkbox" />',
  },
  sortingOrder: ["desc", "asc"],
  animateRows: true,
  columnMenu: LEGACY_COLUMN_MENU,
  // AG Grid новее 25-й определяет тип данных колонки сам, и логическое поле получает
  // отрисовку чекбоксом — она перекрывает наш valueFormatter, который выводит «Да».
  // Так поехали «Совм» у сотрудников, «Непр» у должностей, признак заказа у
  // поставщиков и «Основные» в банковских реквизитах. Выключаем определение типа:
  // колонки рисуются значением, как до апгрейда.
  defaultColDef: { cellDataType: false },
});

export const OrderTableAgGridOptions = (figma = false): GridOptions => ({
  icons: {
    sortAscending: TableIcon("fa-caret-up", figma ? FIGMA_TABLE_ICON_SIZE : 14),
    sortDescending: TableIcon("fa-caret-down", figma ? FIGMA_TABLE_ICON_SIZE : 14),
    // checkboxChecked: '<GeneralIcon type="general-checkbox" />',
    // checkboxUnchecked: '<GeneralIcon type="general-zero-checkbox" />',
  },
  sortingOrder: ["desc", "asc"],
  columnMenu: LEGACY_COLUMN_MENU,
  treeData: true,
  groupDefaultExpanded: 1,
  // С 31-й версии строки-контейнеры при прокрутке прилипают к верхнему краю.
  // В 25-й этого не было, и таблица договоров рассчитана на прежнее поведение:
  // строка портфеля уезжает вверх вместе со своими заказами.
  suppressGroupRowsSticky: true,
  // По умолчанию AG Grid дорисовывает строки и колонки, открывшиеся при прокрутке,
  // порциями по кадрам: таблица на мгновение пустая, потом заполняется. Почти все
  // ячейки здесь рисуют React-компоненты, и на медленной машине и в dev пустота
  // держится долго. Без порций кадр показывается уже заполненным; полное заполнение
  // по замерам не позже, чем с порциями, плавность прокрутки колесом та же.
  suppressAnimationFrame: true,
  getDataPath: (data) => data.path,
  animateRows: true,
  autoGroupColumnDef: {
    headerName: "Наименование",
    field: "name",
    minWidth: 100,
    cellRenderer: "agGroupCellRenderer",
    sortable: true,
    filter: "agTextColumnFilter",
    filterParams: {
      treeList: true,
      keyCreator: (params: any) => (params.value ? params.value.join("#") : null),
    },
    cellRendererParams: {
      checkbox: false,
      suppressCount: true,
    },
  },
  // postSort: (nodes: IRowNode[]) => {
  //   let rowNodes = nodes;
  //   // here we put Ireland rows on top while preserving the sort order
  //   let nextInsertPos = 0;
  //   for (let i = 0; i < rowNodes.length; i++) {
  //     const data = rowNodes[i].data;
  //     // console.info(rowNodes[i]);
  //     if (data.pathName) {
  //       if (rowNodes[i] && rowNodes[i].childrenAfterGroup) {
  //         const childItem = rowNodes[i].childrenAfterGroup;
  //         // console.info(childItem ? childItem[0] : 0);
  //       }
  //       rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
  //       nextInsertPos++;
  //     }
  //   }
  // },
  rowDragManaged: false,
});

export const ROW_HEIGHT = 20;
export const HEADER_HEIGHT = 29;

export const FIGMA_ROW_HEIGHT = 22;
export const FIGMA_HEADER_HEIGHT = 26;
